/* Slice 7 — locale-aware formatters.
 *
 * Every UI-visible number, date, currency, percentage, duration and
 * pluralised label routes through one of these helpers so switching
 * between EN and AR flips the correct locale WITHOUT changing digits:
 * Latin/Western digits stay Latin in both languages so accounting
 * columns line up (matches the `.num-cell` LTR-in-RTL rule from
 * Slice 3).
 *
 * All helpers accept an explicit `lang` argument. In React code, pass
 * `useI18n().lang` — never rely on a global. That keeps the helpers
 * pure and unit-testable under Node without a DOM.
 *
 * Note on locale IDs — every locale ID uses the ``-u-nu-latn`` Unicode
 * extension to FORCE Latin/Western digits even for `ar-*`. Without it,
 * `ar-EG` defaults to Arabic-Indic digits (٠١٢٣…) which break the
 * accounting-column alignment described above and the reviewer's
 * explicit rule for Slice 7.
 */

const LOCALE_MAP = {
  en: "en-US-u-nu-latn",
  ar: "ar-EG-u-nu-latn",
};

const resolveLocale = (lang) => LOCALE_MAP[lang] || LOCALE_MAP.en;

// -----------------------------------------------------------------------
// Dates — STEP 3 · rule 7 (B-7c): lib/dt.js is the one date formatter (DD-MMM-YYYY / HH:mm in the firm zone). The
// locale formats that lived here ("Feb 22, 2026", "02/22/2026" — US order) are gone; the old names point at lib/dt.
// -----------------------------------------------------------------------
export { fmtDate, fmtDateTime } from "./dt";

// -----------------------------------------------------------------------
// Numbers / currency / percent
// -----------------------------------------------------------------------

/**
 * Format a numeric value with locale-aware thousands separators and
 * decimals. Latin digits in both locales.
 *
 * @param {number|string} value           null/NaN → ""
 * @param {"en"|"ar"} lang
 * @param {{minimumFractionDigits?: number,
 *          maximumFractionDigits?: number,
 *          signDisplay?: "auto"|"never"|"always"|"exceptZero"}} opts
 */
export function fmtNumber(value, lang = "en", opts = {}) {
  const n = _toNumber(value);
  if (n === null) return "";
  return _noNegativeZero(new Intl.NumberFormat(resolveLocale(lang), {
    minimumFractionDigits: opts.minimumFractionDigits,
    maximumFractionDigits: opts.maximumFractionDigits,
    signDisplay: opts.signDisplay,
  }).format(n));
}

// Batch 8.1 · F-37: a formatted amount whose digits are all zero never carries a minus sign ("-0.00" → "0.00",
// "‎-٠٫٠٠" → "٠٫٠٠"). Only the display changes; a real non-zero amount keeps its sign.
export function _noNegativeZero(str) {
  if (typeof str !== "string" || !/[\-−]/.test(str)) return str;
  const digits = str.replace(/[^0-9٠-٩۰-۹]/g, "");      // Latin + Arabic-Indic digits only
  if (!digits || /[1-9١-٩۱-۹]/.test(digits)) return str;  // a real amount keeps its sign
  return str.replace(/[\-−]/g, "");
}

/**
 * Format a numeric value as currency. Default USD; override per call
 * (audit engagements use per-firm currency, so callers pass the ISO
 * code from `engagement.currency`).
 */
export function fmtCurrency(value, currency = "USD", lang = "en", opts = {}) {
  const n = _toNumber(value);
  if (n === null) return "";
  // PR 45 review · D2: an engagement's "Other" currency (e.g. "L") is not an ISO code — Intl would throw; show the text as
  // stored before the number instead
  if (!/^[A-Za-z]{3}$/.test(String(currency || ""))) {
    const num = fmtNumber(n, lang, { minimumFractionDigits: opts.minimumFractionDigits ?? 2, maximumFractionDigits: opts.maximumFractionDigits ?? 2 });
    return String(currency || "").trim() ? `${String(currency).trim()} ${num}` : num;
  }
  return _noNegativeZero(new Intl.NumberFormat(resolveLocale(lang), {
    style: "currency", currency,
    minimumFractionDigits: opts.minimumFractionDigits ?? 2,
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
  }).format(n));
}

/**
 * Format a decimal fraction (0.42) as a percentage ("42%"). Pass a raw
 * integer via ``asDecimal: false`` to treat 42 → "42%".
 */
export function fmtPct(value, lang = "en", { asDecimal = true,
                                                 minimumFractionDigits = 0,
                                                 maximumFractionDigits = 1 } = {}) {
  const n = _toNumber(value);
  if (n === null) return "";
  const v = asDecimal ? n : n / 100;
  return new Intl.NumberFormat(resolveLocale(lang), {
    style: "percent",
    minimumFractionDigits, maximumFractionDigits,
  }).format(v);
}

function _toNumber(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

// -----------------------------------------------------------------------
// Durations
// -----------------------------------------------------------------------

/**
 * Format a duration in **minutes** as "{h}h {m}m" — English fixed-form
 * ("2h 30m") for LTR contexts, Arabic uses the same numeric shape but
 * with Arabic labels ("٢h ٣٠m" would be wrong per the Latin-digit
 * rule — we render "2 ساعة 30 دقيقة" instead).
 *
 * Handles the four canonical audit cases from Timesheets, Presence,
 * MyTimeSummary, TimeChip and Executive:
 *   0     → "0m"        (or "0 دقيقة")
 *   45    → "45m"       (or "45 دقيقة")
 *   60    → "1h 0m"     (or "1 ساعة 0 دقيقة")
 *   135   → "2h 15m"    (or "2 ساعة 15 دقيقة")
 */
export function fmtDuration(minutes, lang = "en") {
  const n = _toNumber(minutes);
  if (n === null) return "";
  const m = Math.max(0, Math.round(n));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (lang === "ar") {
    // L2 (M3h) — compact form "H س · M د" avoids Arabic plural-agreement
    // land-mines for hours (ساعة / ساعتان / ساعات) and minutes (دقيقة /
    // دقيقتان / دقائق) which would need Intl.PluralRules per token.
    if (h === 0) return `${fmtNumber(mm, "ar")} د`;
    return `${fmtNumber(h, "ar")} س · ${fmtNumber(mm, "ar")} د`;
  }
  if (h === 0) return `${mm}m`;
  return `${h}h ${mm}m`;
}

// -----------------------------------------------------------------------
// Plurals
// -----------------------------------------------------------------------

/**
 * Locale-aware plural selection using Intl.PluralRules.
 *
 * English cardinal plural categories: ``one``, ``other``.
 * Arabic cardinal plural categories:  ``zero``, ``one``, ``two``,
 *                                       ``few``, ``many``, ``other``.
 *
 * Callers pass a `forms` object with those keys; missing Arabic
 * categories fall back to ``other`` (this covers the reviewer's
 * "few/many collapse to other" question — YES they do here unless
 * the caller explicitly provides them, keeping call sites terse).
 *
 * The token ``{n}`` in the returned string is replaced with the
 * locale-formatted count (Latin digits per Slice 7 rule).
 *
 * Example:
 *   fmtPlural(3, "en", { one: "{n} row", other: "{n} rows" })
 *     → "3 rows"
 *   fmtPlural(3, "ar", { one: "{n} صف", other: "{n} صفوف" })
 *     → "3 صفوف"
 */
export function fmtPlural(count, lang = "en", forms = {}) {
  const n = _toNumber(count);
  if (n === null) return "";
  const abs = Math.abs(n);
  const cat = new Intl.PluralRules(resolveLocale(lang)).select(abs);
  const form = forms[cat] ?? forms.other ?? forms.one ?? "";
  return String(form).replace("{n}", fmtNumber(n, lang));
}
